import { EnhancedRealmMetadata } from '~/utils/cloudHelper';
import { AuthClient, AuthClientOpts } from './clients';
import { Auth0ClientWrapper } from './clients/Auth0';
import { OpenIDClient } from './clients/OpenID';

const exhaustAuthClientType = (type: never) => {
  throw new Error(`Unknown type ${type}`);
};

export const createAuthClient = async (
  opts: AuthClientOpts,
  metadata: EnhancedRealmMetadata,
): Promise<AuthClient> => {
  const { type, redirectUri } = opts;
  const { isSSO, enableRefreshTokens, clientId, oidcServerDiscoveryURI } = metadata;

  switch (type) {
    case 'dev_auth0':
      return new Auth0ClientWrapper({
        client_id: import.meta.env.VITE_AUTH0_CLIENT_ID,
        domain: import.meta.env.VITE_AUTH0_DOMAIN,
        redirect_uri: redirectUri,
      });
    case 'dev_oidc':
      if (oidcServerDiscoveryURI === undefined) {
        throw Error('Missing OIDC Discovery URI: VITE_DEV_OIDC_DISCOVERY_URI must be specified');
      }

      if (clientId === undefined) {
        throw Error('Missing clientId: VITE_DEV_OIDC_CLIENT_ID must be specified');
      }

      return new OpenIDClient({
        clientId,
        discoveryUri: oidcServerDiscoveryURI,
        redirectUri,
        allowRefreshTokensWithRotation: enableRefreshTokens,
      });
    case 'dynamic': {
      if (!isSSO) {
        return new Auth0ClientWrapper({
          client_id: import.meta.env.VITE_AUTH0_CLIENT_ID,
          domain: import.meta.env.VITE_AUTH0_DOMAIN,
          redirect_uri: redirectUri,
        });
      }

      if (oidcServerDiscoveryURI === undefined) {
        // The oidcDiscovery URI is returned by the virtual room's /meta endpoint
        // This could be a virtual room misconfiguration as it must have an OIDC discovery URL
        throw Error('Missing OIDC Discovery URI');
      }

      if (clientId === undefined) {
        // The clientId is returned by the virtual room's /meta endpoint
        // This could be a virtual room misconfiguration as it must have a client ID
        throw Error('Missing clientId');
      }

      return new OpenIDClient({
        clientId,
        discoveryUri: oidcServerDiscoveryURI,
        redirectUri,
        allowRefreshTokensWithRotation: enableRefreshTokens,
      });
    }
    default:
      exhaustAuthClientType(type);
  }

  console.warn(`Unknown auth provider ${type}: fallback to auth0`);
  return new Auth0ClientWrapper({
    client_id: import.meta.env.VITE_AUTH0_CLIENT_ID,
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    redirect_uri: redirectUri,
  });
};
